import { IconButton } from "@mui/material";
import { SettingsPermissions } from "@redotech/account-common/permissions";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import { TriggerState } from "@redotech/redo-return-core-common/return-flow-step-valid";
import { TriggerType } from "@redotech/redo-return-core-common/return-flow/trigger-enums";
import { triggerTypeLabel } from "@redotech/redo-return-ui/flows/return-flow/trigger";
import {
  StepId,
  StepTypeDetailsProps,
} from "@redotech/redo-return-ui/flows/step";
import { Tooltip } from "@redotech/redo-web/arbiter-components/tooltip/tooltip";
import QuestionIcon from "@redotech/redo-web/icon-old/question.svg";
import { SelectDropdown } from "@redotech/redo-web/select-dropdown";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";

const getTriggers = (flowType: FlowType): TriggerType[] => {
  if (flowType === FlowType.RULE) {
    return [
      TriggerType.CONVERSATION_MESSAGE_CREATED,
      TriggerType.MERCHANT_MESSAGE_CREATED,
      TriggerType.CONVERSATION_CREATED,
      TriggerType.CHAT_CONVERTED_TO_EMAIL,
    ];
  } else {
    return [];
  }
};

const getInfo = (type: TriggerType, t: (key: string) => string) => {
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (type) {
    case TriggerType.CONVERSATION_CREATED: {
      return t("This only fires once for a ticket, when it is created.");
    }
    case TriggerType.CONVERSATION_MESSAGE_CREATED: {
      return t(
        "This fires when a ticket is created, and every time the customer sends a new message",
      );
    }
    case TriggerType.MERCHANT_MESSAGE_CREATED: {
      return t("This fires every time a support agent sends a new message");
    }
    // No default
  }
  return null;
};

export function triggerTypeLabelWithTooltip(
  type: TriggerType,
  t: (key: string) => string,
) {
  const labelText = triggerTypeLabel(type);
  const info = getInfo(type, t);
  return info ? (
    <div>
      <Tooltip title={info}>
        <IconButton>
          <QuestionIcon />
        </IconButton>
      </Tooltip>
      {labelText}
    </div>
  ) : (
    labelText
  );
}

export const TriggerDetails = memo(function TriggerDetails({
  stepSelect,
  state,
  setState,
  flowType,
}: StepTypeDetailsProps<TriggerState>) {
  const { t } = useTranslation();
  const canEditReturnsSettings = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
  );

  const nextStepChange = useHandler((next: StepId | undefined) => {
    setState((state) => ({ ...state, next }));
  });
  const handleType = useHandler(async (triggerType: TriggerType) => {
    setState((state) => ({ ...state, triggerType }));
  });

  const triggers = getTriggers(flowType);
  return (
    <>
      <Text fontSize="xs" textColor="secondary">
        {t("Type")}
      </Text>
      <SelectDropdown
        disabled={!canEditReturnsSettings}
        options={triggers}
        value={state.triggerType}
        valueChange={handleType}
      >
        {(option) => triggerTypeLabelWithTooltip(option, t)}
      </SelectDropdown>
      <Text fontSize="xs" textColor="secondary">
        {t("Next step")}
      </Text>
      {!state.next && (
        <Text fontSize="xs" fontWeight="regular" textColor="error">
          {t("Required")}
        </Text>
      )}
      {stepSelect({ value: state.next, valueChange: nextStepChange })}
    </>
  );
});
